import { AGREEMENT_OPPORTUNITY, AGREEMENT_STATUS } from './agreementDictionary'
import {
  APPOINTMENT_STATUS,
  APPOINTMENT_METHOD,
  APPOINTMENT_OUTCOME,
  APPOINTMENT_DECISION
} from './appointmentDictionary'
import { PHONE_NUMBER_TYPES } from './communicationDictionary'
import { CALL_AGENT_BADGES } from './gamificationDictionary'
import { NOTIFICATION_TYPES } from './notificationDictionary'
import { EXTERNAL_SYSTEMS } from './systemDictionary'
import { AGREEMENT_TIMELINE_TYPES } from './timelineDictionary'

// Standardized base interface that all dictionary items must implement
export interface BaseDictionaryItem {
  key: string
  label?: string
  localeKey?: string
  icon?: string
  color?: string
}

// Registry mapping string keys to actual dictionary collections
const DICTIONARY_REGISTRY = {
  agreementStatus: AGREEMENT_STATUS,
  agreementOpportunity: AGREEMENT_OPPORTUNITY,
  appointmentStatus: APPOINTMENT_STATUS,
  appointmentMethod: APPOINTMENT_METHOD,
  appointmentOutcome: APPOINTMENT_OUTCOME,
  appointmentDecision: APPOINTMENT_DECISION,
  callAgentBadges: CALL_AGENT_BADGES,
  notificationTypes: NOTIFICATION_TYPES,
  phoneNumberTypes: PHONE_NUMBER_TYPES,
  externalSystems: EXTERNAL_SYSTEMS,
  agreementTimelineTypes: AGREEMENT_TIMELINE_TYPES
} as const

// Type for resource keys used by the dictionary
export type DictionaryResource = keyof typeof DICTIONARY_REGISTRY

/**
 * Retrieves a dictionary item based on the provided resource and key.
 *
 * @param resource - The dictionary resource to search in
 * @param key - The key to match the item
 * @returns The properties of the matching item, or an error object if no match is found
 */
export function getDictionaryItem<T extends BaseDictionaryItem = BaseDictionaryItem>(
  resource: DictionaryResource,
  key: string
): T | BaseDictionaryItem {
  const errorObject: BaseDictionaryItem = {
    key: 'ERROR',
    label: 'ERROR',
    localeKey: 'dev.dataMappingError',
    icon: 'bug',
    color: 'error'
  }

  if (!resource || !key) {
    console.error('DictionaryUtils: Missing arguments: resource and key')
    return errorObject
  }

  const resourceItems = DICTIONARY_REGISTRY[resource]
  if (!resourceItems) {
    console.error(`DictionaryUtils: Resource ${resource} not found`)
    return { ...errorObject, label: `Resource ${resource} not found` }
  }

  const item = (resourceItems as readonly BaseDictionaryItem[]).find((item: BaseDictionaryItem) => item.key === key)
  if (!item) {
    console.error(`DictionaryUtils: Key ${key} not found in resource ${resource}`)
    return {
      ...errorObject,
      label: `Key ${key} not found in resource ${resource}`
    }
  }

  return item as unknown as T
}

/**
 * Retrieves all options for a given dictionary resource.
 *
 * @param resource - The dictionary resource to get options for
 * @returns Array of all items for the resource
 */
export function getDictionaryOptions<T extends BaseDictionaryItem = BaseDictionaryItem>(
  resource: DictionaryResource
): readonly T[] {
  const resourceItems = DICTIONARY_REGISTRY[resource]
  if (!resourceItems) {
    console.error(`DictionaryUtils: Resource ${resource} not found`)
    return [] as readonly T[]
  }

  return resourceItems as unknown as readonly T[]
}

// Removed legacy alias to keep dictionary naming consistent
